import type {
  BandedRatingResult,
  RatingCellOption,
  RatingScaleOption,
} from '@risksmart-app/i18n/src/ratings';
import {
  getRatingByBandedLikelihoodAndImpact,
  getRatingByBandedValue,
} from '@risksmart-app/i18n/src/ratings';

/**
 * The browser's entry point to the shared band rule for an overall rating.
 *
 * A thin pass-through by design: the unassessed-coordinate guard (the Default
 * and Risk Aggregation Only models persist `0` for a dimension that was never
 * assessed) lives inside the shared rule, not here, so the server and the
 * browser cannot drift apart on it — see SPEC decision D-a and the parity
 * assertion in RSP-7286.
 */
export const bandRatingCell = <T extends RatingCellOption>(
  options: readonly T[],
  likelihood: number | null | undefined,
  impact: number | null | undefined
): BandedRatingResult<T> =>
  getRatingByBandedLikelihoodAndImpact(options, likelihood, impact);

/** The same, for a single dimension. */
export const bandDimensionValue = <T extends RatingScaleOption>(
  options: readonly T[],
  value: number | null | undefined
): T | undefined => getRatingByBandedValue(options, value);

/**
 * Taxonomy option sets type their value as `number | string | null`; the band
 * rule needs a numeric scale, so keep only the options that declare one.
 */
export const numericRatingOptions = <T extends { value: unknown }>(
  options: readonly T[]
): (T & { value: number })[] =>
  options.filter(
    (option): option is T & { value: number } =>
      typeof option.value === 'number'
  );
